import React, { FC, useCallback, useContext, useMemo, useState } from "react"
import { CourseListFilters } from "../../generated/graphql"
import { buildFilterStorageKey, loadStoredFilters, persistFilters } from "../../helpers/filterPersistence"
import { buildLastViewedCourseKey } from "../../helpers/lastViewedCourse"

/**
 * Zentrale Single-Source-of-Truth für den Kursfilter-State.
 *
 * Der Provider lebt auf Modul-Ebene STABIL direkt über dem Router-Switch in
 * App.tsx. Dadurch überlebt der Filterzustand jeden App-Re-Render und jeden
 * Route-Wechsel innerhalb desselben Embeds — und wird zugleich per Embed über
 * sessionStorage persistiert (deterministischer Storage-Key aus dem Seed).
 */
type CourseFilterContextValue = {
    courseFilters: CourseListFilters
    setCourseFilters: React.Dispatch<React.SetStateAction<CourseListFilters>>
    // Per-Embed Storage-Key für den zuletzt angesehenen Kurs (Scroll-zurück-UX).
    lastViewedCourseKey: string
}

export const CourseFilterContext = React.createContext<CourseFilterContextValue | undefined>(undefined)

type CourseFilterProviderProps = {
    seed: CourseListFilters
    children: React.ReactNode
}

export const CourseFilterProvider: FC<CourseFilterProviderProps> = ({ seed, children }) => {
    // Deterministischer, über Renders stabiler Storage-Key aus dem Seed (iFrame-Params).
    const storageKey = buildFilterStorageKey(seed)
    // Eigener Key für den zuletzt angesehenen Kurs (gleiches Seed-Scoping, eigener Prefix).
    const lastViewedCourseKey = buildLastViewedCourseKey(seed)

    // Lazy-Init läuft genau EINMAL: gespeicherter Zustand als Basis, der Seed
    // spreadet ZULETZT — explizite iFrame-Parameter gewinnen über den
    // persistierten Wert (Precedence der Embed-Konfiguration).
    const [courseFilters, setCourseFiltersState] = useState<CourseListFilters>(() => ({
        ...loadStoredFilters(storageKey),
        ...seed,
    }))

    // Write-Through-Setter mit stabiler Identität: persistiert bei JEDER Änderung,
    // niemals beim Init. Unterstützt sowohl Wert- als auch Updater-Form.
    const setCourseFilters = useCallback<React.Dispatch<React.SetStateAction<CourseListFilters>>>(
        (action) => {
            setCourseFiltersState((prev) => {
                const next = typeof action === "function" ? (action as (p: CourseListFilters) => CourseListFilters)(prev) : action
                persistFilters(storageKey, next)
                return next
            })
        },
        [storageKey]
    )

    // Memoisierter Context-Value: verhindert unnötige Re-Renders/Refetch-Stürme
    // bei Konsumenten, solange sich weder State noch Setter-Identität ändern.
    const value = useMemo(
        () => ({ courseFilters, setCourseFilters, lastViewedCourseKey }),
        [courseFilters, setCourseFilters, lastViewedCourseKey]
    )

    return <CourseFilterContext.Provider value={value}>{children}</CourseFilterContext.Provider>
}

/**
 * Convenience-Hook: liefert den Context-Value oder undefined (kein Provider).
 * Konsumenten (z.B. CourseTableContainer) lösen den undefined-Fall über einen
 * internen Fallback-State auf, damit Tests ohne Provider weiterlaufen.
 */
export function useCourseFilterContext(): CourseFilterContextValue | undefined {
    return useContext(CourseFilterContext)
}
